import { assetUrl } from './assetUrl.js';

const ROOT_ID = 'slot-start-screen';

/**
 * Pragmatic-style post-load intro: feature screenshot, max-win / RTP chips, Continue + sound.
 * @param {{
 *   mountEl?: HTMLElement | null,
 *   onContinue?: () => void,
 *   onSoundToggle?: () => void,
 * }} [opts]
 */
export function createGameStartScreen({
  mountEl = typeof document !== 'undefined' ? document.body : null,
  onContinue,
  onSoundToggle,
  logoSrc = '/assets/Logo0.png',
  shotSrc = '/assets/max-win-expanded.png',
} = {}) {
  if (!mountEl || typeof document === 'undefined') {
    return {
      show: () => {},
      hide: () => {},
      isVisible: () => false,
      update: () => {},
      dispose: () => {},
      el: null,
    };
  }

  let root = document.getElementById(ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = ROOT_ID;
    root.className = 'slot-start-screen';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.innerHTML = `
      <div class="slot-start-screen__backdrop" data-start-dismiss="1" aria-hidden="true"></div>
      <div class="slot-start-screen__shell">
        <header class="slot-start-screen__header">
          <img
            class="slot-start-screen__logo"
            src="${assetUrl('/assets/Logo0.png')}"
            alt=""
            decoding="async"
            draggable="false"
          />
          <h1 class="slot-start-screen__title"></h1>
        </header>
        <div class="slot-start-screen__hero">
          <div class="slot-start-screen__hero-glow" aria-hidden="true"></div>
          <div class="slot-start-screen__shot-frame">
            <span class="slot-start-screen__preview-badge" aria-hidden="true"></span>
            <img
              class="slot-start-screen__shot"
              src="${assetUrl(shotSrc)}"
              alt=""
              decoding="async"
              draggable="false"
            />
          </div>
          <p class="slot-start-screen__feature slot-start-screen__feature--hidden">
            <span class="slot-start-screen__feature-label"></span>
          </p>
        </div>
        <footer class="slot-start-screen__footer">
          <div class="slot-start-screen__footer-actions">
            <div class="slot-start-screen__meta">
              <span class="slot-start-screen__max-win"></span>
              <span class="slot-start-screen__rtp"></span>
            </div>
            <button type="button" class="slot-start-screen__continue"></button>
            <button type="button" class="slot-start-screen__sound" aria-label="">
              <img
                class="slot-start-screen__sound-icon slot-start-screen__sound-icon--on"
                src="${assetUrl('/assets/sound_on.png')}"
                alt=""
                decoding="async"
                draggable="false"
              />
              <img
                class="slot-start-screen__sound-icon slot-start-screen__sound-icon--off"
                src="${assetUrl('/assets/sound_off.png')}"
                alt=""
                decoding="async"
                draggable="false"
              />
            </button>
          </div>
          <p class="slot-start-screen__hint"></p>
        </footer>
      </div>
    `;
    mountEl.appendChild(root);
  }

  const titleEl = root.querySelector('.slot-start-screen__title');
  const featureEl = root.querySelector('.slot-start-screen__feature');
  let featureLabelEl = root.querySelector('.slot-start-screen__feature-label');
  if (!featureLabelEl && featureEl) {
    const legacyText = featureEl.textContent?.trim() ?? '';
    featureEl.textContent = '';
    featureLabelEl = document.createElement('span');
    featureLabelEl.className = 'slot-start-screen__feature-label';
    if (legacyText) featureLabelEl.textContent = legacyText;
    featureEl.appendChild(featureLabelEl);
  }
  const maxWinEl = root.querySelector('.slot-start-screen__max-win');
  const rtpEl = root.querySelector('.slot-start-screen__rtp');
  const continueBtn = root.querySelector('.slot-start-screen__continue');
  const soundBtn = root.querySelector('.slot-start-screen__sound');
  const hintEl = root.querySelector('.slot-start-screen__hint');
  const previewBadgeEl = root.querySelector('.slot-start-screen__preview-badge');

  let visible = false;

  const syncSoundIcon = (soundOn) => {
    root.classList.toggle('slot-start-screen--sound-off', !soundOn);
  };

  const dismiss = () => {
    if (!visible) return;
    onContinue?.();
  };

  const onContinueClick = (e) => {
    e.stopPropagation();
    dismiss();
  };

  const onSoundClick = (e) => {
    e.stopPropagation();
    onSoundToggle?.();
  };

  const onRootClick = (e) => {
    if (!visible) return;
    const target = e.target;
    if (!(target instanceof Element)) return;
    // Only the sound toggle is interactive without closing; everything else dismisses.
    if (target.closest('.slot-start-screen__sound')) return;
    dismiss();
  };

  continueBtn?.addEventListener('click', onContinueClick);
  soundBtn?.addEventListener('click', onSoundClick);
  root.addEventListener('click', onRootClick);

  return {
    el: root,
    show() {
      visible = true;
      root.classList.remove('slot-start-screen--intro');
      void root.offsetWidth;
      root.classList.add('slot-start-screen--visible', 'slot-start-screen--intro');
      root.toggleAttribute('inert', false);
      window.getSelection?.()?.removeAllRanges?.();
      continueBtn?.focus({ preventScroll: true });
    },
    hide() {
      visible = false;
      root.classList.remove('slot-start-screen--visible', 'slot-start-screen--intro');
      root.toggleAttribute('inert', true);
    },
    isVisible: () => visible,
    /**
     * @param {{
     *   gameTitle?: string,
     *   featureLabel?: string,
     *   maxWinLabel?: string,
     *   rtpLabel?: string,
     *   continueLabel?: string,
     *   tapHint?: string,
     *   previewBadge?: string,
     *   soundOn?: boolean,
     *   soundLabel?: string,
     * }} patch
     */
    update(patch = {}) {
      if (patch.gameTitle != null && titleEl) titleEl.textContent = patch.gameTitle;
      if (patch.featureLabel != null) {
        if (featureLabelEl) featureLabelEl.textContent = patch.featureLabel;
        featureEl?.classList.toggle('slot-start-screen__feature--hidden', !patch.featureLabel);
      }
      if (patch.maxWinLabel != null && maxWinEl) {
        maxWinEl.textContent = patch.maxWinLabel;
        maxWinEl.classList.toggle('slot-start-screen__meta-chip--hidden', !patch.maxWinLabel);
      }
      if (patch.rtpLabel != null && rtpEl) {
        rtpEl.textContent = patch.rtpLabel;
        rtpEl.classList.toggle('slot-start-screen__meta-chip--hidden', !patch.rtpLabel);
      }
      if (patch.continueLabel != null && continueBtn) continueBtn.textContent = patch.continueLabel;
      if (patch.tapHint != null && hintEl) {
        hintEl.textContent = patch.tapHint;
        hintEl.classList.toggle('slot-start-screen__hint--hidden', !patch.tapHint);
      }
      if (patch.previewBadge != null && previewBadgeEl) {
        previewBadgeEl.textContent = patch.previewBadge;
        previewBadgeEl.classList.toggle('slot-start-screen__preview-badge--hidden', !patch.previewBadge);
      }
      if (patch.soundLabel != null && soundBtn) soundBtn.setAttribute('aria-label', patch.soundLabel);
      if (patch.soundOn != null) syncSoundIcon(!!patch.soundOn);
      const label = [titleEl?.textContent, featureLabelEl?.textContent].filter(Boolean).join('. ');
      if (label) root.setAttribute('aria-label', label);
    },
    dispose() {
      continueBtn?.removeEventListener('click', onContinueClick);
      soundBtn?.removeEventListener('click', onSoundClick);
      root.removeEventListener('click', onRootClick);
      root?.remove();
    },
  };
}
